import { useState } from 'react';
import {
  LANDIQ_BOARD_COLUMNS,
  type LandIqBoardColumn,
  type LandIqBoardTicket,
  type LandIqDataRunMetrics,
} from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { BoardColumn, type DropState } from '@/components/landiq/board/board-column';

export const BOARD_GRID_CLASS = 'grid min-w-[1100px] grid-cols-[repeat(5,minmax(0,1fr))] gap-3';

type Props = {
  readonly tickets: ReadonlyArray<LandIqBoardTicket>;
  /** The metrics of each entry's latest run, by entry id; a card shows its open entry's. */
  readonly runMetrics: Readonly<Record<string, LandIqDataRunMetrics>>;
  /** Tickets with a move in flight: their cards are not draggable. */
  readonly inFlight: ReadonlySet<string>;
  readonly onDrop: (ticket: LandIqBoardTicket, to: LandIqBoardColumn) => void;
};

/** Renders LandIQ's answer as it is: a card sits in the column LandIQ names, in LandIQ's order. */
export function LandIqBoard({ tickets, runMetrics, inFlight, onDrop }: Props) {
  const t = useTranslations('pages.admin.landiq-data-runs');
  const [dragged, setDragged] = useState<LandIqBoardTicket | null>(null);
  const [hovered, setHovered] = useState<LandIqBoardColumn | null>(null);
  const byColumn = new Map<LandIqBoardColumn, LandIqBoardTicket[]>();

  for (const ticket of tickets) byColumn.set(ticket.column, [...(byColumn.get(ticket.column) ?? []), ticket]);

  const dropState = (column: LandIqBoardColumn): DropState => {
    if (dragged === null) return 'idle';

    if (column === dragged.next) return hovered === column ? 'hovered' : 'valid';

    return column === dragged.column ? 'idle' : 'invalid';
  };

  const clear = () => {
    setDragged(null);
    setHovered(null);
  };

  const drop = (column: LandIqBoardColumn) => {
    if (dragged !== null && column !== dragged.column) onDrop(dragged, column);

    clear();
  };

  return (
    <div className="h-full min-h-0 overflow-auto bg-canvas p-6">
      <div role="group" aria-label={t('title')} className={BOARD_GRID_CLASS}>
        {LANDIQ_BOARD_COLUMNS.map((column) => (
          <BoardColumn
            key={column}
            column={column}
            tickets={byColumn.get(column) ?? []}
            runMetrics={runMetrics}
            dropState={dropState(column)}
            dragging={dragged?.ticket}
            isDraggable={(ticket) => ticket.next !== null && !inFlight.has(ticket.ticket)}
            onHover={setHovered}
            onDrop={drop}
            onDragStart={setDragged}
            onDragEnd={clear}
          />
        ))}
      </div>
    </div>
  );
}
